import { useEffect } from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";

import { AiChat } from "@src/features/ai/components/AiChat/AiChat";
import { useDispatch, useSelector } from "@src/store";
import {
  getAiChatPresentation,
  getIsAiChatOpen,
  setAiChat,
} from "@src/store/slices/global";

import {
  AgenticLogicTakeover,
  type AgenticLogicTakeoverProps,
} from "./AgenticLogicTakeover";
import { useIsAgenticLogicTakeoverActive } from "./takeoverEntry";

/**
 * The takeover plus the Atlas rail it is authored in.
 *
 * The rail is rendered HERE rather than in the layout shell because every host
 * is a full-page dialog, portalled above that shell — a rail rendered there
 * would be underneath the dialog. Only one `AiChat` may exist at a time (each
 * one opens its own workflow stream and dispatches into the same slice), so
 * this claims the rail for as long as it is mounted and the shell renders
 * none; see `AiChat.railHost`.
 */
export const AgenticLogicTakeoverHost = (props: AgenticLogicTakeoverProps) => {
  const dispatch = useDispatch();
  const isActive = useIsAgenticLogicTakeoverActive();
  // Mounted only while the chat is open, exactly as the layout shell does it.
  // `AiChat` closes itself whenever the store says the panel is shut, so a
  // permanently mounted rail fights `useAtlasChatRail`, which is opening the
  // panel from the other side — one closing and one opening on every commit.
  const isAiChatOpen = useSelector(getIsAiChatOpen);
  // While Atlas designs a rule the chat takes the host's whole content area,
  // exactly as it does in the layout shell; the builder beside it is hidden,
  // not unmounted, so it is ready the moment the first graph lands.
  const chatIsPage =
    useSelector(getAiChatPresentation) === "page" && isAiChatOpen;

  useEffect(() => {
    if (!isActive) {
      return;
    }
    dispatch(setAiChat({ key: "railHost", value: "embedded" }));
    return () => {
      dispatch(setAiChat({ key: "railHost", value: "shell" }));
    };
  }, [dispatch, isActive]);

  if (!isActive) {
    return null;
  }

  return (
    // `height: 100%` as well as `flex: 1`: the hosts are dialogs, and their
    // content slots are not all flex columns — one stretches this, the other
    // fills it.
    <Stack
      direction="row"
      sx={{ flex: 1, height: "100%", minHeight: 0, minWidth: 0 }}
    >
      <Box
        sx={{
          display: chatIsPage ? "none" : "flex",
          flex: 1,
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <AgenticLogicTakeover {...props} />
      </Box>
      {isAiChatOpen ? (
        <Box
          component="aside"
          aria-label="AI chat"
          sx={{
            flex: chatIsPage ? 1 : undefined,
            flexShrink: 0,
            height: "100%",
          }}
        >
          <AiChat />
        </Box>
      ) : null}
    </Stack>
  );
};
